/**
 * Task actions that start from a single task, rather than from a view's task list.
 *
 * The taskApi helpers take the project's full `currentTasks` list because they write
 * it back to projectTasksCache optimistically. Surfaces like TaskDetailPopover only
 * hold the one task, and render inside many different views, so these wrappers
 * resolve the list from the cache (fetching it if the project was never loaded) and
 * then delegate. Every view that lists tasks follows the cache, so they all update.
 *
 * Each action takes optional `pendingUpdates` — unsaved edits from the popover —
 * and persists them *before* acting. For moves this ordering is required: the task
 * update is addressed through the source project's URL, which stops resolving once
 * the task has moved.
 */

import type { Task } from './types';
import { fetchProjectTasks, getCachedTasks, setProjectTasks } from './stores/projectTasksCache';
import { openProjectModalForConvertFromTask } from './stores/ui';
import { duplicateTask, moveTaskToProject, updateTask } from './taskApi';
import { jsonHeaders } from './apiHelpers';
import { toast } from 'svelte-sonner';

/** The task's project list, guaranteed to contain the task. */
async function projectTasksFor(task: Task): Promise<Task[]> {
  const cached = getCachedTasks(task.projectId);
  const tasks = cached.fetched ? cached.tasks : await fetchProjectTasks(task.projectId);
  return tasks.some((t) => t.id === task.id) ? tasks : [...tasks, task];
}

/** Save unsaved edits (if any) and return the project's up-to-date task list. */
async function persistPendingEdits(task: Task, pendingUpdates?: Partial<Task>): Promise<Task[]> {
  const tasks = await projectTasksFor(task);
  if (!pendingUpdates || Object.keys(pendingUpdates).length === 0) return tasks;

  const result = await updateTask(task.projectId, task.id, pendingUpdates, tasks);
  return result.success ? result.tasks : tasks;
}

/** Copy the task into its own project (after saving unsaved edits, so the copy includes them). */
export async function duplicateTaskFromDetails(
  task: Task,
  pendingUpdates?: Partial<Task>,
): Promise<void> {
  const tasks = await persistPendingEdits(task, pendingUpdates);
  await duplicateTask(task.projectId, task.id, tasks);
}

/** Move the task to another project, with the usual undo toast. */
export async function moveTaskFromDetails(
  task: Task,
  targetProjectId: string,
  targetProjectTitle: string,
  pendingUpdates?: Partial<Task>,
): Promise<void> {
  if (targetProjectId === task.projectId) return;
  const tasks = await persistPendingEdits(task, pendingUpdates);
  await moveTaskToProject(task.projectId, targetProjectId, task.id, tasks, { targetProjectTitle });
}

/**
 * Open the project modal prefilled from the task; the task is removed once the new
 * project is created. Unsaved edits are saved first so a cancelled conversion keeps them.
 */
export async function convertTaskToProject(
  task: Task,
  pendingUpdates?: Partial<Task>,
): Promise<void> {
  await persistPendingEdits(task, pendingUpdates);
  const title = pendingUpdates?.title ?? task.title;
  const notes = pendingUpdates?.notes !== undefined ? pendingUpdates.notes : task.notes;
  openProjectModalForConvertFromTask(title, notes || '', () =>
    removeTaskAfterConversion({ ...task, title }),
  );
}

/**
 * Removes the source task after a successful conversion. No undo is offered: the new
 * project already exists, so restoring the task would leave a duplicate (task +
 * project) rather than cleanly reversing the conversion.
 */
export async function removeTaskAfterConversion(task: Task): Promise<void> {
  const previous = await projectTasksFor(task);
  setProjectTasks(
    task.projectId,
    previous.filter((t) => t.id !== task.id),
  );

  try {
    const response = await fetch(`/projects/${task.projectId}/tasks/${task.id}`, {
      method: 'DELETE',
      headers: jsonHeaders(),
    });
    if (!response.ok) throw new Error('Server error');
    toast.success(`Converted "${task.title}" to project`);
  } catch {
    setProjectTasks(task.projectId, previous);
    toast.error('Project created, but failed to remove the original task');
  }
}
